import React, {memo} from 'react';
import {MUIBox, MUITypography} from '@/components/MUI-Components';
import {GOOGLE_SEARCH_URL} from '@/constants/urls';

// ─── Social & brand SVG icons ─────────────────────────────────────────────────
// Thin React wrappers around inline SVG markup. These are platform-wide share
// and brand icons — NOT blog-specific. Any page that needs social sharing or
// the Google CTA widget can import directly from here.
//
// Brand icon colours (Google logo) are intentionally hardcoded — they are the
// official brand specification and must NOT change with theme tokens.
//
// All icons are wrapped in React.memo() so they never re-render when a parent
// re-renders with identical props.

export const GoogleGIcon = memo(() => (
    <svg className="bdn-google-icon" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" width="20" height="20">
        <path
            d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
            fill="#4285F4"
        />
        <path
            d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
            fill="#34A853"
        />
        <path
            d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"
            fill="#FBBC05"
        />
        <path
            d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
            fill="#EA4335"
        />
    </svg>
));
GoogleGIcon.displayName = 'GoogleGIcon';

export const ShareXIcon = memo(() => (
    <svg viewBox="0 0 24 24" fill="currentColor" width="15" height="15">
        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.744l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
    </svg>
));
ShareXIcon.displayName = 'ShareXIcon';

export const ShareLinkedInIcon = memo(() => (
    <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
        width="15"
        height="15"
    >
        <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z" />
        <rect x="2" y="9" width="4" height="12" />
        <circle cx="4" cy="4" r="2" />
    </svg>
));
ShareLinkedInIcon.displayName = 'ShareLinkedInIcon';

export const ShareFacebookIcon = memo(() => (
    <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
        width="15"
        height="15"
    >
        <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z" />
    </svg>
));
ShareFacebookIcon.displayName = 'ShareFacebookIcon';

export const ShareInstagramIcon = memo(() => (
    <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
        width="15"
        height="15"
    >
        <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
        <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
        <line x1="17.5" y1="6.5" x2="17.51" y2="6.5" />
    </svg>
));
ShareInstagramIcon.displayName = 'ShareInstagramIcon';

/** LinkedIn badge glyph used in author/reviewer cards. */
export const LinkedInBadgeIcon = memo(() => (
    <svg viewBox="0 0 24 24" fill="currentColor" width="14" height="14">
        <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 0 1-2.063-2.065 2.064 2.064 0 1 1 2.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
    </svg>
));
LinkedInBadgeIcon.displayName = 'LinkedInBadgeIcon';

const openGoogleSearch = () => globalThis.open(GOOGLE_SEARCH_URL, '_blank', 'noopener noreferrer');

/**
 * "Make us preferred source on Google" CTA.
 * Usable on any content page — blog, case study, news article, etc.
 */
export const GoogleSourceCta = memo(({className}: {className?: string}) => (
    <MUIBox
        className={`bdn-google-cta${className ? ` ${className}` : ''}`}
        role="button"
        tabIndex={0}
        onClick={openGoogleSearch}
        onKeyDown={(e: React.KeyboardEvent) => {
            if (e.key === 'Enter' || e.key === ' ') openGoogleSearch();
        }}
    >
        <GoogleGIcon />
        <MUITypography className="bdn-google-text">Make us preferred source on Google</MUITypography>
    </MUIBox>
));
GoogleSourceCta.displayName = 'GoogleSourceCta';
